import React, { useEffect, useMemo, useState } from "react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { Download } from "lucide-react";
import { FULL_MONTHS, classNames, qaStatusColor } from "../../lib/utils";
import { StatChip, downloadCSV } from "./_shared";

/**
 * QA Audits report — filtered by (home, month, year).
 *
 * `/qa/dashboard` returns the full year of audits for the scoped home(s)
 * (House-Manager scoping is applied server-side). We narrow client-side by
 * `audit_month === month` so the on-screen table, the audit count chip, and
 * the CSV export always share the same selection.
 */
export default function QAReport({ home, month, year }) {
  const [yearAudits, setYearAudits] = useState([]);

  useEffect(() => {
    api.get("/qa/dashboard", { params: { home: home || "ALL", year } })
      .then(({ data }) => setYearAudits(data.audits || []))
      .catch(() => setYearAudits([]));
  },
  // eslint-disable-next-line react-hooks/exhaustive-deps
  [home, year]);

  const audits = useMemo(
    () => yearAudits.filter((a) => Number(a.audit_month) === Number(month)),
    [yearAudits, month]
  );

  const periodLabel = `${FULL_MONTHS[month - 1]} ${year}`;
  const homeLabel = home || "All Homes";

  const exportCSV = () => {
    const data = audits.map((a) => ({
      home: a.home,
      month: a.audit_month,
      year: a.audit_year,
      overall_score_percent: a.overall_score_percent,
      status: a.overall_status,
      completed_by: a.completed_by,
      manager_notes: a.manager_notes || "",
    }));
    const fname = `nv-qa-audits-${year}-${String(month).padStart(2, "0")}-${(home || "ALL").replace(/\s+/g, "_")}.csv`;
    downloadCSV(fname, data);
    toast.success("CSV downloaded");
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap gap-2">
          <StatChip label="Audits" value={audits.length} tone="sky" data-testid="qa-audit-count" />
          <StatChip label="Period" value={periodLabel} tone="slate" />
          <StatChip label="Home" value={homeLabel} tone="slate" />
        </div>
        <button
          onClick={exportCSV}
          disabled={!audits.length}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-sky-800 hover:bg-sky-900 text-white text-sm font-semibold disabled:opacity-50"
          data-testid="export-qa-csv"
        >
          <Download size={14} /> Export CSV
        </button>
      </div>
      <div className="nv-card overflow-x-auto">
        <table className="w-full text-sm" data-testid="qa-table">
          <thead className="bg-slate-50 text-slate-600 text-xs uppercase tracking-wider">
            <tr>
              <th className="px-5 py-3 text-left">Home</th>
              <th className="px-3 py-3 text-left">Period</th>
              <th className="px-3 py-3 text-right">Score</th>
              <th className="px-3 py-3 text-left">Status</th>
              <th className="px-3 py-3 text-left">Completed by</th>
            </tr>
          </thead>
          <tbody>
            {audits.map((a) => (
              <tr key={a.id} className="border-t border-slate-100" data-testid={`qa-row-${a.home}-${a.audit_month}-${a.audit_year}`}>
                <td className="px-5 py-3 font-medium text-slate-800">{a.home}</td>
                <td className="px-3 py-3 text-slate-600">{FULL_MONTHS[a.audit_month - 1]} {a.audit_year}</td>
                <td className="px-3 py-3 text-right font-bold tabular-nums">{a.overall_score_percent}%</td>
                <td className="px-3 py-3">
                  <span className={classNames("inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium border", qaStatusColor(a.overall_status))}>
                    {a.overall_status}
                  </span>
                </td>
                <td className="px-3 py-3 text-slate-600">{a.completed_by}</td>
              </tr>
            ))}
            {audits.length === 0 && (
              <tr>
                <td colSpan={5} className="px-5 py-10 text-center text-slate-500" data-testid="qa-empty-state">
                  No QA audits recorded for <span className="font-semibold text-slate-700">{homeLabel}</span> in <span className="font-semibold text-slate-700">{periodLabel}</span>.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
